// @ts-nocheck

import { find } from 'lodash';
import { createSelector } from 'reselect';

import { selectBetFilterId } from '../../selectors/betFilter/betFilterPrimary.selector';
import { selectEntitiesCategoriesEntity } from '../../selectors/entities/categories/entitiesCategoriesPrimary.selector';
import { selectEntitiesFilters } from '../../selectors/entities/filters/entitiesFiltersPrimary.selector';
import { selectEntitiesSportsEntity } from '../../selectors/entities/sports/entitiesSportsPrimary.selector';
import { selectEntitiesTournamentsEntity } from '../../selectors/entities/tournaments/entitiesTournamentsPrimary.selector';

const emptyArray: [] = [];

const getFilterIds = createSelector(
  [selectEntitiesSportsEntity, selectEntitiesCategoriesEntity, selectEntitiesTournamentsEntity],
  (sport, category, tournament) => {
    if (tournament) {
      return tournament.filters ?? emptyArray;
    }

    if (category) {
      return category.filters ?? emptyArray;
    }

    return sport?.filters ?? emptyArray;
  },
);

const getRouteFilters = createSelector([selectEntitiesFilters, getFilterIds], (filters, filterIds) => {
  const currentFilters = [];

  if (filterIds?.length) {
    filterIds.forEach((id) => {
      if (filters[id]) {
        currentFilters.push(filters[id]);
      }
    });
  }

  return currentFilters;
});

const getUserFilter = createSelector([selectEntitiesFilters, selectBetFilterId], (filters, filterId) => {
  const filter = filters[filterId];
  let parentFilter = null;

  if (filter && filter.betFilterParentId) {
    parentFilter = filters[filter.betFilterParentId];
  }

  return {
    filter,
    parentFilter,
  };
});

const formatFilters = (filters) =>
  filters.map((f) => ({
    id: f.betFilterId,
    label: f.betFilterName,
    effectiveId: f.effectiveFilterId,
  }));

const sortFilters = (arr) => arr.sort((a, b) => a.displayOrder - b.displayOrder);

const getEffectiveFilterId = (filterId, filters) => {
  const secondLevels = filters.filter((f) => f.betFilterParentId === filterId);
  const secondLevelDefault = secondLevels.filter((f) => f.betFilterIsDefault);

  if (secondLevelDefault.length) {
    return secondLevelDefault[0].betFilterId;
  }

  if (secondLevels.length) {
    return sortFilters(secondLevels)[0].betFilterId;
  }

  return null;
};

// if we have a second level default filter, we add effective id on first level filter
const getEffectivesFilters = (firstLevels, filters) =>
  firstLevels.map((firstLevel) => ({
    ...firstLevel,
    effectiveFilterId: getEffectiveFilterId(firstLevel.betFilterId, filters),
  }));

const selectBetFiltersProps = createSelector(
  [getRouteFilters, selectBetFilterId, getUserFilter],
  (routeFilters, userFilterId, userFilters) => {
    let firstLevelId = null;
    let secondLevelId = null;
    let fallbackId = null;
    let secondLevelFilters = [];
    let firstLevelFilters = routeFilters.filter((filter) => filter.betFilterParentId === null);

    if (userFilterId) {
      const userFilterInRoute = find(routeFilters, (f) => f.betFilterId === userFilterId);

      if (userFilterInRoute) {
        const isFirstLevel = userFilterInRoute.betFilterParentId === null;
        firstLevelId = isFirstLevel ? userFilterInRoute.betFilterId : userFilterInRoute.betFilterParentId;
        secondLevelFilters = routeFilters.filter((filter) => filter.betFilterParentId === firstLevelId);

        if (!isFirstLevel) {
          secondLevelId = userFilterInRoute.betFilterId;
        } else {
          if (secondLevelFilters.length > 0) {
            const defaultSecondLevelFilter = find(secondLevelFilters, (f) => f.betFilterIsDefault === 1);

            if (defaultSecondLevelFilter) {
              secondLevelId = defaultSecondLevelFilter?.betFilterId || null;
            } else {
              secondLevelId = secondLevelFilters[0].betFilterId || null;
              fallbackId = secondLevelId;
            }
          } else if (secondLevelFilters[0]) {
            secondLevelId = secondLevelFilters[0].betFilterId;
          }
        }
      } else {
        const { filter, parentFilter } = userFilters;

        if (parentFilter) {
          firstLevelId = parentFilter.betFilterId;
          firstLevelFilters = [parentFilter, ...firstLevelFilters];
          secondLevelId = filter.betFilterId;
          secondLevelFilters = routeFilters.filter((filter) => filter.betFilterParentId === firstLevelId);
          secondLevelFilters = [filter, ...secondLevelFilters];
        } else if (filter) {
          firstLevelId = filter.betFilterId;
          firstLevelFilters = [filter, ...firstLevelFilters];
        }
      }
    }

    sortFilters(firstLevelFilters);
    sortFilters(secondLevelFilters);

    const effectivesFilter = getEffectivesFilters(firstLevelFilters, routeFilters);

    firstLevelFilters = formatFilters(effectivesFilter);
    secondLevelFilters = formatFilters(secondLevelFilters);

    return {
      firstLevelFilters,
      secondLevelFilters,
      firstLevelId,
      secondLevelId,
      fallbackId,
    };
  },
);

export default selectBetFiltersProps;
